import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Button,
  Center,
  Flex,
  Group,
  Text,
  TextInput,
  Tooltip,
  rem,
} from "@mantine/core";
import { IconPencil, IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import { Avatar } from "@aws-amplify/ui-react";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { getUrl } from "aws-amplify/storage";
import {
  GraphQLQuery,
  GraphQLSubscription,
  generateClient,
} from "aws-amplify/api";
import {
  CasesByPIDAndCreatedAtQuery,
  CasesByPIDAndCreatedAtQueryVariables,
  GetPatientQuery,
  ModelSortDirection,
  OnCreateCaseSubscription,
  OnCreateCaseSubscriptionVariables,
  OnDeleteCaseSubscription,
  OnDeleteCaseSubscriptionVariables,
  OnUpdateCaseSubscription,
  OnUpdateCaseSubscriptionVariables,
  OnUpdatePatientSubscription,
  OnUpdatePatientSubscriptionVariables,
  TemplatesByLIDAndUpdatedAtQuery,
  TemplatesByLIDAndUpdatedAtQueryVariables,
} from "../API";
import BoxLoader from "../components/BoxLoader";
import AddNewCaseModal from "../components/Case/AddNewCaseModal";
import CaseTable from "../components/Case/CaseTable/CaseTable";
import ConfirmDeletePatientModal from "../components/Patient/ConfirmDeletePatientModal";
import EditPatientModal from "../components/Patient/EditPatientModal";
import { casesByPIDAndCreatedAtShallow } from "../graphql/casesByPIDAndCreatedAtShallow";
import { getPatientShallow } from "../graphql/getPatientShallow";
import { getLocation, templatesByLIDAndUpdatedAt } from "../graphql/queries";
import {
  onCreateCase,
  onDeleteCase,
  onUpdateCase,
  onUpdatePatient,
} from "../graphql/subscriptions";
import { sortByCreatedAt, sortByUpdatedAt } from "../utils/utils";

const client = generateClient();

/* eslint-disable @typescript-eslint/no-explicit-any */
const Cases = () => {
  const uLocation = useLocation();

  const currentPatient = uLocation.state;

  if (currentPatient == null) {
    window.location.href = "/patients";
  }
  const items = [
    { title: currentPatient?.name, href: "/patients", isActive: false },
    { title: "Cases", href: "", isActive: true },
  ].map((item, index) =>
    item.href ? (
      <NavLink
        to={item.href}
        className={item.isActive ? "active" : ""}
        key={index}
      >
        {item.title}
      </NavLink>
    ) : (
      <span className={item.isActive ? "active" : ""} key={index}>
        {item.title}
      </span>
    ),
  );
  const { patientId } = useParams();
  const [patient, setPatient] = useState<any>(currentPatient);

  const [cases, setCases] = useState<any>([]);
  const [refreshing, setRefreshing] = useState(true);

  const [isAddCaseModalVisible, setIsAddCaseModalVisible] = useState(false);
  const [isEditPatientModalVisible, setIsEditPatientModalVisible] =
    useState(false);
  const [location, setLocation] = useState<any>();
  const [search, setSearch] = useState("");
  // might be alright without lower case only

  const [nextToken, setNextToken] = useState("");
  const queryLimit = 20;

  useEffect(() => {
    fetchData();
  }, []);

  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    try {
      const userAttr: FetchUserAttributesOutput = await fetchUserAttributes();
      const templatesQueryInput: TemplatesByLIDAndUpdatedAtQueryVariables = {
        lID: userAttr["custom:location"] as string,
        sortDirection: ModelSortDirection.DESC,
        filter: {
          _deleted: { ne: true },
          archived: { ne: true },
        },
      };
      const templatesData: any = await client.graphql<
        GraphQLQuery<TemplatesByLIDAndUpdatedAtQuery>
      >({ query: templatesByLIDAndUpdatedAt, variables: templatesQueryInput });
      const templatesList = templatesData.data.templatesByLIDAndUpdatedAt.items;

      setTemplates(templatesList.sort(sortByUpdatedAt) as any);
    } catch (err) {
      console.warn(err);
    }
  }

  async function fetchData() {
    setRefreshing(true);
    try {
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      if (userInfo["custom:location"] === undefined) {
        setRefreshing(false);
        return;
      }
      const locationData = await client.graphql({
        query: getLocation,
        variables: { id: userInfo["custom:location"] },
      });
      const locationItem = locationData.data.getLocation;
      setLocation(locationItem);

      // get cases
      const caseQueryInput: CasesByPIDAndCreatedAtQueryVariables = {
        pID: patientId!,
        limit: queryLimit,
        sortDirection: ModelSortDirection.DESC,
      };
      const caseData: any = await client.graphql<
        GraphQLQuery<CasesByPIDAndCreatedAtQuery>
      >({ query: casesByPIDAndCreatedAtShallow, variables: caseQueryInput });
      const caseList = caseData.data.casesByPIDAndCreatedAt.items;

      setCases(caseList.sort(sortByCreatedAt));
      setNextToken(caseData.data.casesByPIDAndCreatedAt.nextToken);
      
      // Trigger case migration to Supabase when viewing patient's cases
      if (caseList.length > 0) {
        console.log(`[CASE-MIGRATION] Found ${caseList.length} cases for patient ${patientId} to migrate to Supabase`);
        
        // Migrate cases in background (don't await to avoid blocking UI)
        setTimeout(async () => {
          try {
            const { RelationshipMigrationService } = await import("@/services/RelationshipMigrationService");
            const orchestrator = RelationshipMigrationService.getInstance();
            
            // Ensure patient exists first, then migrate cases with relationship awareness
            await orchestrator.ensurePatientExists(patientId);
            
            // Batch migrate cases using orchestrator (augment with pID/templateId where needed)
            for (let i = 0; i < caseList.length; i++) {
              const caseItem = caseList[i];
              const enriched = {
                ...caseItem,
                pID: (caseItem as any).pID || patientId,
                templateId: (caseItem as any).caseTemplateId || (caseItem as any).templateId
              } as Record<string, unknown>;
              try {
                console.log(`[CASE-MIGRATION] Migrating case ${i + 1}/${caseList.length}:`, caseItem.title);
                await orchestrator.migrateCaseEnsuringPatient(
                  enriched,
                  { data: { createCase: enriched } } as Record<string, unknown>
                );
                console.log(`[CASE-MIGRATION] Successfully migrated case:`, caseItem.title);
              } catch (caseError) {
                console.warn(`[CASE-MIGRATION] Failed to migrate case ${caseItem.title}:`, caseError);
              }
              
              // Small delay between cases to prevent overwhelming
              if (i < caseList.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            }
            console.log(`[CASE-MIGRATION] Batch case migration completed`);
          } catch (error) {
            console.warn('[CASE-MIGRATION] Migration orchestrator failed:', error);
          }
        }, 1200); // Start after 1.2 seconds
      }
      
      fetchPatient();
      // Set up subscription to Cases table
      const varsCreateCase: OnCreateCaseSubscriptionVariables = {
        filter: {
          pID: { eq: patientId },
        },
      };
      const createSub = client
        .graphql<GraphQLSubscription<OnCreateCaseSubscription>>({
          query: onCreateCase,
          variables: varsCreateCase,
        })
        .subscribe({
          next: async ({ data }) => {
            const newCase = data.onCreateCase;
            fetchCases();
            
            // Migrate new case with relationship awareness
            if (newCase) {
              try {
                const { RelationshipMigrationService } = await import("@/services/RelationshipMigrationService");
                const orchestrator = RelationshipMigrationService.getInstance();
                
                // Ensure patient exists first, then migrate the new case
                await orchestrator.ensurePatientExists(patientId);
                const enriched = {
                  ...newCase,
                  pID: (newCase as any).pID || patientId,
                  templateId: (newCase as any).caseTemplateId || (newCase as any).templateId
                } as Record<string, unknown>;
                await orchestrator.migrateCaseEnsuringPatient(
                  enriched,
                  { data: { createCase: enriched } } as Record<string, unknown>
                );
                console.log('[CASE-MIGRATION] Successfully migrated new case from subscription:', (newCase as any).title);
              } catch (error) {
                console.warn('[CASE-MIGRATION] Failed to migrate new case from subscription:', error);
              }
            }
          },
          error: (error) => console.warn(error),
        });

      const varsUpdateCase: OnUpdateCaseSubscriptionVariables = {
        filter: {
          pID: { eq: patientId },
        },
      };
      // Set up subscription to Cases table
      const updateSub = client
        .graphql<GraphQLSubscription<OnUpdateCaseSubscription>>({
          query: onUpdateCase,
          variables: varsUpdateCase,
        })
        .subscribe({
          next: async () => {
            fetchCases();
          },
          error: (error) => console.warn(error),
        });

      const varsDeleteCase: OnDeleteCaseSubscriptionVariables = {
        filter: {
          pID: { eq: patientId },
        },
      };
      const deleteSub = client
        .graphql<GraphQLSubscription<OnDeleteCaseSubscription>>({
          query: onDeleteCase,
          variables: varsDeleteCase,
        })
        .subscribe({
          next: () => {
            fetchCases();
          },
          error: (error) => console.warn(error),
        });

      const varsUpdatePatient: OnUpdatePatientSubscriptionVariables = {
        filter: {
          id: { eq: patientId },
        },
      };
      const updatePatientSub = client
        .graphql<GraphQLSubscription<OnUpdatePatientSubscription>>({
          query: onUpdatePatient,
          variables: varsUpdatePatient,
        })
        .subscribe({
          next: () => {
            fetchPatient();
          },
          error: (error) => console.warn(error),
        });

      return () => {
        createSub.unsubscribe();
        updateSub.unsubscribe();
        deleteSub.unsubscribe();
        updatePatientSub.unsubscribe();
      };
    } catch (err) {
      console.warn(err);
    }
    setRefreshing(false);
  }

  const fetchPatient = async () => {
    setRefreshing(true);
    try {
      // Get patients
      const getPatientData = await client.graphql<
        GraphQLQuery<GetPatientQuery>
      >({ query: getPatientShallow, variables: { id: patientId } });
      const newPatient: any = getPatientData.data.getPatient;

      let s3Image;
      try {
        s3Image = (
          await getUrl({
            path: `public/${newPatient.image}`,
            options: { validateObjectExistence: true },
          })
        ).url.toString();
      } catch (err) {
        console.warn(err);
        s3Image = (
          await getUrl({
            path: `public/pet-image-placeholder.png`,
            options: { validateObjectExistence: true },
          })
        ).url.toString();
      }
      newPatient["s3Image"] = s3Image;
      setPatient(newPatient);
      
      // Trigger patient migration to Supabase when viewing individual patient
      if (newPatient?.id) {
        setTimeout(async () => {
          try {
            const { DatabaseMigrationAdapter } = await import("@/services/migration");
            const migrationAdapter = DatabaseMigrationAdapter.getInstance();
            
            console.log('[PATIENT-MIGRATION] Migrating individual patient:', newPatient.name);
            await migrationAdapter.handlePatientOperation('CREATE', newPatient as Record<string, unknown>, { data: { createPatient: newPatient } } as Record<string, unknown>);
            console.log('[PATIENT-MIGRATION] Successfully migrated individual patient to Supabase');
          } catch (error) {
            console.warn('[PATIENT-MIGRATION] Failed to migrate individual patient:', error);
          }
        }, 800); // Start after 0.8 seconds
      }
    } catch (err) {
      console.warn(err);
    }
    setRefreshing(false);
  };

  async function fetchCases() {
    setLoading(true);
    try {
      const caseQueryInput: CasesByPIDAndCreatedAtQueryVariables = {
        pID: patientId!,
        limit: queryLimit,
        sortDirection: ModelSortDirection.DESC,
      };
      const caseData: any = await client.graphql<
        GraphQLQuery<CasesByPIDAndCreatedAtQuery>
      >({ query: casesByPIDAndCreatedAtShallow, variables: caseQueryInput });
      const caseList = caseData.data.casesByPIDAndCreatedAt.items;
      setCases(caseList.sort(sortByCreatedAt));
      setNextToken(caseData.data.casesByPIDAndCreatedAt.nextToken);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  }

  const fetchMoreCases = async () => {
    // if the next token is null, theres no data left. or there was an error loading the initial data
    if (nextToken === null || nextToken == "") return;
    setLoading(true);
    try {
      const caseQueryInput: CasesByPIDAndCreatedAtQueryVariables = {
        pID: patientId!,
        limit: queryLimit,
        nextToken: nextToken,
        sortDirection: ModelSortDirection.DESC,
      };
      const caseData: any = await client.graphql<
        GraphQLQuery<CasesByPIDAndCreatedAtQuery>
      >({ query: casesByPIDAndCreatedAtShallow, variables: caseQueryInput });
      const caseList = caseData.data.casesByPIDAndCreatedAt.items;
      setCases(cases.concat(caseList).sort(sortByCreatedAt));
      setNextToken(caseData.data.casesByPIDAndCreatedAt.nextToken);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  const searchCases = async () => {
    setLoading(true);
    try {
      // list by location
      const caseQueryInput: CasesByPIDAndCreatedAtQueryVariables = {
        pID: patientId!,
        limit: queryLimit,
        sortDirection: ModelSortDirection.DESC,
        filter: {
          or: [
            { title: { contains: search } },
            { description: { contains: search } },
            { historySummary: { contains: search } },
            { planSummary: { contains: search } },
          ],
        },
      };
      const caseData: any = await client.graphql<
        GraphQLQuery<CasesByPIDAndCreatedAtQuery>
      >({ query: casesByPIDAndCreatedAtShallow, variables: caseQueryInput });
      const caseList = caseData.data.casesByPIDAndCreatedAt.items;
      setCases(caseList.sort(sortByCreatedAt));
      setNextToken(caseData.data.casesByPIDAndCreatedAt.nextToken);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  const searchMoreCases = async () => {
    // if the next token is null, theres no data left. or there was an error loading the initial data
    if (nextToken === null || nextToken == "") return;
    setLoading(true);
    try {
      // list by location
      const caseQueryInput: CasesByPIDAndCreatedAtQueryVariables = {
        pID: patientId!,
        limit: queryLimit,
        sortDirection: ModelSortDirection.DESC,
        filter: {
          or: [
            { title: { contains: search } },
            { description: { contains: search } },
            { historySummary: { contains: search } },
            { planSummary: { contains: search } },
          ],
        },
        nextToken: nextToken,
      };
      const caseData: any = await client.graphql<
        GraphQLQuery<CasesByPIDAndCreatedAtQuery>
      >({ query: casesByPIDAndCreatedAtShallow, variables: caseQueryInput });
      const caseList = caseData.data.casesByPIDAndCreatedAt.items;
      setCases(cases.concat(caseList).sort(sortByCreatedAt));
      setNextToken(caseData.data.casesByPIDAndCreatedAt.nextToken);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  const searchIcon = <IconSearch style={{ width: rem(16), height: rem(16) }} />;
  const inputSearchRef = useRef<any>(null);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);
  const onClearSearch = () => {
    if (inputSearchRef.current != null) {
      inputSearchRef.current.value = "";
    }
    setSearch("");
    if (shouldFetchData) {
      fetchCases();
      setShouldFetchData(false);
    }
  };
  const onSearchInputChange = (event: any) => {
    let { value } = event.currentTarget;
    if (value) {
      value = value.trim().toLowerCase();
    }
    setSearch(value);
  };
  const [loading, setLoading] = useState(false);
  return (
    <Box p={15}>
      <Breadcrumbs separatorMargin={3} mt={0} separator="→">
        {items}
      </Breadcrumbs>
      <Group
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex",
          width: "100%",
          maxHeight: "100%",
        }}
      >
        {/* Header */}
        <Group
          className="header-patients"
          flex={1}
          mt={10}
          align="center"
          w={"100%"}
          justify="space-between"
        >
          <Flex align={"center"}>
            {patient && (
              <>
                <Avatar src={patient.s3Image} />
                <Text fz={24} fw={"bold"} mx={5}>
                  {patient?.name || ""}
                </Text>
                <Tooltip withArrow label="Edit patient">
                  <ActionIcon
                    variant="subtle"
                    radius={20}
                    size={"md"}
                    p={3}
                    onClick={() => setIsEditPatientModalVisible(true)}
                  >
                    <IconPencil color="gray" size={"lg"} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Flex>

          <Group className="header-patients-action">
            <TextInput
              leftSection={searchIcon}
              style={{ minWidth: "320px" }}
              placeholder="Search case names and descriptions"
              radius={20}
              ref={inputSearchRef}
              onChange={onSearchInputChange}
              rightSection={
                search && (
                  <ActionIcon
                    radius={"50%"}
                    onClick={onClearSearch}
                    size={"sm"}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                )
              }
            ></TextInput>
            {search && (
              <Button
                radius={20}
                onClick={() => {
                  setShouldFetchData(true);
                  searchCases();
                }}
                fw={400}
                variant="default"
              >
                <IconPlus width={16} />
                <Text fw={400} fz={14}>
                  Search all cases
                </Text>
              </Button>
            )}
            <Button
              radius={20}
              onClick={() => setIsAddCaseModalVisible(true)}
              fw={400}
            >
              <IconPlus width={16} />
              <Text fw={400} fz={14}>
                Add new case
              </Text>
            </Button>
          </Group>
        </Group>
        <Flex
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            minHeight: "calc(100vh - 300px)",
          }}
        >
          {refreshing ? (
            <BoxLoader />
          ) : cases.length == 0 ? (
            <Center pos={"relative"}>
              <Flex direction={"column"} justify={"center"} ta={"center"}>
                <p>No cases found</p>
                <p>Please add some cases</p>
                <Button
                  radius={20}
                  onClick={() => setIsAddCaseModalVisible(true)}
                  fw={400}
                >
                  <IconPlus width={16} />
                  <Text fw={400} fz={14}>
                    Add new case
                  </Text>
                </Button>
                <Box mt={16}>
                  <Box>
                    <Text fw={400}>Tip:</Text> You can search for patients by
                    name or client name.
                  </Box>
                </Box>
              </Flex>
            </Center>
          ) : (
            <Group
              style={{
                width: "100%",
                display: "flex",
                flex: "1",
                alignItems: "center",
                position: "relative",
                justifyContent: "center",
              }}
            >
              <CaseTable
                data={cases}
                isLoading={loading}
                search={search}
                patientId={patientId}
                patient={patient}
                templates={templates}
                loadMoreCases={search ? searchMoreCases : fetchMoreCases}
                fetchCases={search ? searchCases : fetchCases}
              />
              {loading && <BoxLoader />}
            </Group>
          )}
        </Flex>
        <EditPatientModal
          modalOpened={isEditPatientModalVisible}
          setModalOpened={setIsEditPatientModalVisible}
          patient={patient}
          fetchPatients={fetchPatient}
          setPatient={setPatient}
          setModalConfirmDeleteOpened={setModalConfirmDeleteOpened}
        />
        <ConfirmDeletePatientModal
          modalOpened={modalConfirmDeleteOpened}
          setModalOpened={setModalConfirmDeleteOpened}
          patient={patient}
          fetchPatients={fetchPatient}
        />
        <AddNewCaseModal
          location={location}
          modalOpened={isAddCaseModalVisible}
          setModalOpened={setIsAddCaseModalVisible}
          setPatient={setPatient}
          patient={patient}
          templates={templates}
        />
      </Group>
    </Box>
  );
};
export default Cases;
